import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export type AbsenceType = 'VACATION' | 'SICK' | 'UNPAID' | 'OTHER';
export type AbsenceStatus = 'REQUESTED' | 'APPROVED' | 'REJECTED';

export interface Absence {
  id: string;
  type: AbsenceType;
  status: AbsenceStatus;
  dateFrom: string;
  dateTo: string;
  halfDay: boolean;
  days: number;
  comment: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  employee?: { id: string; displayName: string };
}

export interface LeaveBalance {
  year: number;
  entitlementDays: number;
  takenDays: number;
  pendingDays: number;
  remainingDays: number;
  sickDays: number;
  unpaidDays: number;
}

export const useMyAbsences = (year: number) =>
  useQuery({
    queryKey: ['absence', 'mine', year],
    queryFn: () => api.get<Absence[]>(`/absence/mine?year=${year}`),
  });

export const usePendingAbsences = () =>
  useQuery({
    queryKey: ['absence', 'pending'],
    queryFn: () => api.get<Absence[]>('/absence/pending'),
  });

export const useLeaveBalance = (year: number) =>
  useQuery({
    queryKey: ['absence', 'leave', year],
    queryFn: () => api.get<LeaveBalance>(`/working-time/leave?year=${year}`),
  });

/**
 * Absence changes invalidate working time as well as absence.
 *
 * An approved absence changes every expected/worked figure in the month it covers,
 * so refreshing only the absence list would leave the month view stating a balance
 * that no longer holds.
 */
function useAbsenceMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['absence'] });
      void queryClient.invalidateQueries({ queryKey: ['working-time'] });
    },
  });
}

export interface AbsenceDraft {
  type: AbsenceType;
  dateFrom: string;
  dateTo: string;
  halfDay?: boolean;
  comment?: string | null;
}

export const useRequestAbsence = () =>
  useAbsenceMutation((draft: AbsenceDraft) => api.post<Absence>('/absence', draft));

export const useCancelAbsence = () =>
  useAbsenceMutation((id: string) => api.delete(`/absence/${id}`));

export const useApproveAbsence = () =>
  useAbsenceMutation((id: string) => api.post(`/absence/${id}/approve`, {}));

export const useRejectAbsence = () =>
  useAbsenceMutation(({ id, comment }: { id: string; comment: string }) =>
    api.post(`/absence/${id}/reject`, { comment }),
  );
